import { useState, useEffect, useCallback } from 'react';
import { TRIAL_CTA, TRIAL_TERMS } from "../config/pricing";
import {
    Dialog, DialogContent, Box, Typography,
    Button, IconButton, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';
import { config } from '../Constants';

const URL = config.url;

const FreeTrialPopup = () => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { isAuthenticated, isPremium, authToken, login, user } = useAuth();

    const startCheckout = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${URL}/api/v1/subscription/create-checkout?trial=true`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                localStorage.setItem('freeTrialPopupDismissed', Date.now().toString());
                window.location.href = data.data.sessionUrl;
            } else {
                console.error('Error creating checkout session');
                setLoading(false);
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
            setLoading(false);
        }
    }, [authToken]);

    // Handle the redirect after OAuth login
    useEffect(() => {
        const pendingTrial = localStorage.getItem('pendingFreeTrial');
        
        if (pendingTrial && isAuthenticated && !isPremium && authToken && !user?.hasUsedFreeTrial) {
            localStorage.removeItem('pendingFreeTrial');
            startCheckout();
        } else if (pendingTrial && user?.hasUsedFreeTrial) {
            // User already used trial, clear the pending flag
            localStorage.removeItem('pendingFreeTrial');
        }
    }, [isAuthenticated, isPremium, authToken, user, startCheckout]);

    // Scroll-triggered popup
    useEffect(() => {
        // Don't show if premium or already used trial
        if (isPremium || user?.hasUsedFreeTrial) return;
        
        const hasSeenPopup = localStorage.getItem('freeTrialPopupDismissed');
        if (hasSeenPopup) return;

        const handleScroll = () => {
            const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
            
            if (scrollPercent > 35) {
                setOpen(true);
                window.removeEventListener('scroll', handleScroll);
            }
        };

        const timer = setTimeout(() => {
            window.addEventListener('scroll', handleScroll);
        }, 2000);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isPremium, user]);

    const handleClose = () => {
        setOpen(false);
        localStorage.setItem('freeTrialPopupDismissed', Date.now().toString());
    };

    const handleClaimTrial = async () => {
        if (!isAuthenticated) {
            // Set flag before OAuth so we know to redirect after
            localStorage.setItem('pendingFreeTrial', 'true');
            login();
            return;
        }

        await startCheckout();
    };

    // Don't render if premium or already used trial
    if (isPremium || user?.hasUsedFreeTrial) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    overflow: 'hidden',
                    maxWidth: 520,
                    width: 'calc(100% - 16px)',
                    bgcolor: 'var(--background-color-light)',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundImage: 'none',
                    position: 'relative',
                    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)'
                }
            }}
        >
            <IconButton
                onClick={handleClose}
                aria-label="Close"
                disableRipple
                sx={{
                    position: 'absolute',
                    right: 10,
                    top: 10,
                    color: 'text.primary',
                    bgcolor: 'var(--background-color-light)',
                    border: '1px solid',
                    borderColor: 'divider',
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    p: 0,
                    '&:hover': {
                        bgcolor: 'var(--background-color-dark)',
                        color: 'text.primary'
                    },
                    zIndex: 1
                }}
            >
                <CloseIcon sx={{ fontSize: 34 }} />
            </IconButton>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ textAlign: 'center', px: 4, pb: 4.5 }}>
                    <Box
                        sx={{
                            width: '100%',
                            ml: 0,
                            mb: 3,
                            overflow: 'hidden',
                            pt: 2
                        }}
                    >
                        <Box
                            component="img"
                            src="/images/pricing-email.webp"
                            alt="Premium email alerts preview"
                            sx={{
                                width: '100%',
                                display: 'block',
                                mt: -2
                            }}
                        />
                    </Box>

                    <Typography
                        variant="h5"
                        sx={{
                            mb: 2.5,
                            color: 'text.primary',
                            lineHeight: 1.2,
                            fontWeight: 500
                        }}
                    >
                        Know exactly where to pick up with StockMaid premium emails.
                    </Typography>

                    <Button
                        onClick={handleClaimTrial}
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        sx={{
                            bgcolor: 'var(--sm-btn-contained-bg)',
                            color: 'var(--sm-btn-contained-text)',
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 'bold',
                            '&:hover': {
                                bgcolor: 'var(--sm-btn-contained-hover)'
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : TRIAL_CTA}
                    </Button>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
                        {TRIAL_TERMS}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default FreeTrialPopup;
